/**
 * The "Find LinkedIn URN" helper — a DRAGGABLE bookmarklet link plus one-time
 * setup instructions, shown on the operator surfaces (LinkedIn tags review
 * queue + entity Manage tab).
 *
 * Why draggable, not copy-paste: the bookmarklet is a ~2.5k-char blob, and one
 * mangled character (a stray line break, a smart quote, a dropped backslash)
 * makes the button silently do nothing. Dragging the link to the bookmarks bar
 * preserves the href byte-for-byte. The href is set imperatively via a ref
 * because React strips `javascript:` URLs from JSX `href` attributes.
 */

import { useEffect, useRef, useState } from 'react';
import { buildBookmarkletHref } from '../lib/linkedinBookmarklet.js';

export function LinkedInUrnHelper() {
  const [open, setOpen] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (linkRef.current) linkRef.current.href = buildBookmarkletHref();
  }, [open]);

  return (
    <div className="bg-blue-50/60 border border-blue-200 rounded-xl p-4 mb-4 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-grey-900">Need a company's URN? Use the finder button</span>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-xs text-primary-700 hover:underline"
        >
          {open ? 'Hide setup' : 'Show setup'}
        </button>
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        {/* Draggable bookmarklet. onClick is prevented so clicking it here does
            nothing — it is meant to be DRAGGED to the bookmarks bar. */}
        <a
          ref={linkRef}
          href="#"
          onClick={(e) => e.preventDefault()}
          draggable
          className="inline-block bg-primary-600 text-white font-semibold rounded-lg px-4 py-2 cursor-grab select-none"
          title="Drag me to your bookmarks bar"
        >
          Find LinkedIn URN
        </a>
        <span className="text-grey-600">← drag this to your bookmarks bar (one-time)</span>
      </div>

      {open && (
        <ol className="mt-3 list-decimal ml-5 space-y-1 text-grey-700">
          <li>Turn on the bookmarks bar: <b>View → Always Show Bookmarks Bar</b>.</li>
          <li>Drag the blue <b>Find LinkedIn URN</b> button above onto the bookmarks bar.</li>
          <li>
            Make sure you're <b>signed in to LinkedIn</b>, open a company's page, and click the
            bookmark. A box shows the URN with a <b>Copy</b> button and a reverse-check link.
          </li>
          <li>Paste the URN and the exact page name into the fields below, then confirm.</li>
        </ol>
      )}
    </div>
  );
}
